import {
  buildSelectStyles,
  type SelectSize,
} from "components/ui/Select/Select";
import { cn } from "lib/utils";
import type { KeyboardEvent } from "react";
import CreatableSelect from "react-select/creatable";

type TagOption = {
  value: string;
  label: string;
};

export type CreatableMultiSelectWidth = "narrow" | "full";

export type CreatableMultiSelectProps = {
  value?: string[];
  onChange?: (values: string[]) => void;
  onBlur?: () => void;
  name?: string;
  placeholder?: string;
  width?: CreatableMultiSelectWidth;
  size?: SelectSize;
};

const widthClass: Record<CreatableMultiSelectWidth, string> = {
  narrow: "w-40",
  full: "w-full",
};

/**
 * 純粋な CreatableMultiSelect リーフ。タグ入力 UI を提供する。
 * 値は string[] として扱う。
 * @param {CreatableMultiSelectProps} props CreatableMultiSelect の props
 * @return {JSX.Element} 複数選択可能な入力
 */
export const CreatableMultiSelect = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder = "",
  width = "full",
  size = "sm",
}: CreatableMultiSelectProps): JSX.Element => {
  const selectedOptions: TagOption[] = (value ?? []).map(v => ({
    value: v,
    label: v,
  }));

  const emitChange = (next: string[]) => onChange?.(next);

  return (
    <div className={cn(widthClass[width])}>
      <CreatableSelect<TagOption, true>
        name={name}
        isMulti
        options={[]}
        value={selectedOptions}
        placeholder={placeholder}
        onChange={newValue => emitChange(newValue.map(o => o.value))}
        onBlur={onBlur}
        styles={buildSelectStyles<TagOption, true>(size)}
        onCreateOption={inputValue =>
          emitChange([...(value ?? []), inputValue])
        }
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Backspace" && (value?.length ?? 0) > 0) {
            emitChange((value ?? []).slice(0, -1));
          }
        }}
        components={{
          MultiValueContainer: ({ innerProps, children }) => (
            <div
              {...innerProps}
              className="bg-primary rounded h-6 mx-1 flex items-center text-white px-1"
            >
              {children}
            </div>
          ),
          MultiValueLabel: ({ children }) => (
            <div className="text-white text-xs">{children}</div>
          ),
          MultiValueRemove: ({ innerProps, data }) => (
            <div
              {...innerProps}
              onClick={() =>
                emitChange((value ?? []).filter(v => v !== data.value))
              }
              className="px-1 cursor-pointer"
            >
              ×
            </div>
          ),
        }}
      />
    </div>
  );
};
